import { useMutation, useQueryClient } from '@tanstack/react-query'
import { generatePath } from 'react-router-dom'

import {
  EChatV2ServerPath,
  TLatestFormSubmissionResponse,
  TSubmitPreChatFormPayload,
  TSubmitPreChatFormResponse,
} from '~/features/chatbotV2/models'
import {
  appendChatbotMessageFromServer,
  guestChatPost,
  mapFormSubmissionWireToChatMessage,
  markPreChatFormSubmittedForConversation,
  mutationKeys,
  queryKeys,
} from '~/features/chatbotV2/utils'

type TVariables = TSubmitPreChatFormPayload & {
  agentId: string
  conversationId: string
  guestToken: string
  refreshGuestToken?: () => Promise<string | null>
}

const submitPreChatForm = ({
  conversationId,
  guestToken,
  refreshGuestToken,
  formId,
  values,
}: TVariables) =>
  guestChatPost<TSubmitPreChatFormResponse>({
    guestToken,
    path: generatePath(EChatV2ServerPath.conversationFormSubmissions, {
      conversationId,
    }),
    payload: { formId, values },
    refreshToken: refreshGuestToken,
  })

const useSubmitPreChatForm = (conversationId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: mutationKeys.preChatFormSubmit(conversationId),
    mutationFn: submitPreChatForm,
    onSuccess: (data, variables) => {
      if (data.message) {
        const chatMessage = mapFormSubmissionWireToChatMessage(data.message)
        if (chatMessage) {
          appendChatbotMessageFromServer(chatMessage)
        }
      }

      markPreChatFormSubmittedForConversation(
        variables.agentId,
        variables.conversationId,
      )

      // agentId only — see queryKeys.latestFormSubmission's own comment.
      // Previously keyed on variables.guestToken too: if the POST above had
      // self-healed internally (its own 401 refresh), this wrote the "already
      // submitted" cache entry under the STALE token that was passed in, not
      // whatever guestToken is current by the time anything reads it back —
      // an orphaned write under a key nothing will ever look up again
      // (Cursor-flagged, Medium severity).
      const latestFormSubmissionQueryKey = queryKeys.latestFormSubmission({
        agentId: variables.agentId,
      })

      queryClient.setQueryData<TLatestFormSubmissionResponse>(
        latestFormSubmissionQueryKey,
        {
          priorSubmission: {
            values: variables.values,
            submittedAt: new Date().toISOString(),
          },
        },
      )

      queryClient.invalidateQueries({
        queryKey: latestFormSubmissionQueryKey,
      })
    },
  })
}

export default useSubmitPreChatForm
